import React, { useEffect, useMemo, useState } from "react"

import { format } from "date-fns"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import { useSessionDates } from "@/hooks/useSessionDates"
import { useVehicleCollectionSubmissionHandlers } from "@/hooks/useVehicleCollectionSubmissionHandlers"

import VehicleCollectionCalendar from "./vehicle-collection/VehicleCollectionCalendar"
import VehicleCollectionConfirmationSummary from "./vehicle-collection/VehicleCollectionConfirmationSummary"
import VehicleCollectionImportantNotes from "./vehicle-collection/VehicleCollectionImportantNotes"
import VehicleCollectionTimeSelector from "./vehicle-collection/VehicleCollectionTimeSelector"

interface TimeSlot {
  time: string
  fullDate: Date
}

interface VehicleCollectionProps {
  onComplete?: () => void
}

const VehicleCollection: React.FC<VehicleCollectionProps> = ({ onComplete }) => {
  const { application } = useApplication()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    application.vehicleCollection?.date ? new Date(application.vehicleCollection.date) : undefined,
  )
  const { toast } = useToast()

  // Use vehicle collection submission handlers
  const { handleVehicleCollectionSubmit, isSubmitting: isCreatingBooking } =
    useVehicleCollectionSubmissionHandlers()

  // Fetch available vehicle collection session dates using our custom hook
  const {
    data: sessionDates = [],
    isLoading: isLoadingDates,
    error: datesError,
  } = useSessionDates(application?.jobId, "vehicle_collection", application?.applicationId)

  // Pre-select date if already set in application
  useEffect(() => {
    if (application?.vehicleCollection?.date && sessionDates.length > 0) {
      // Parse the stored date string to a Date object
      const collectionDate = new Date(application.vehicleCollection.date)

      // Find the matching session date from available dates
      const matchingSession = sessionDates.find((session) => {
        const sessionDate = session.date
        return (
          sessionDate.getFullYear() === collectionDate.getFullYear() &&
          sessionDate.getMonth() === collectionDate.getMonth() &&
          sessionDate.getDate() === collectionDate.getDate() &&
          sessionDate.getHours() === collectionDate.getHours() &&
          sessionDate.getMinutes() === collectionDate.getMinutes()
        )
      })

      // If we found a matching session, select it
      if (matchingSession) {
        setSelectedDate(matchingSession.date)
      }
    }
  }, [application?.vehicleCollection?.date, sessionDates])

  // Convert session dates to Date objects for the calendar component
  const availableDates = useMemo(() => {
    return sessionDates.map((session) => session.date)
  }, [sessionDates])

  const isDateAvailable = (date: Date) => {
    if (isLoadingDates || !availableDates.length) return false

    return availableDates.some(
      (availableDate) =>
        availableDate.getDate() === date.getDate() &&
        availableDate.getMonth() === date.getMonth() &&
        availableDate.getFullYear() === date.getFullYear(),
    )
  }

  const availableTimesForDate = (date: Date): TimeSlot[] => {
    if (!date) return []
    return availableDates
      .filter(
        (availableDate) =>
          availableDate.getDate() === date.getDate() &&
          availableDate.getMonth() === date.getMonth() &&
          availableDate.getFullYear() === date.getFullYear(),
      )
      .map((d) => ({
        time: format(d, "h:mm a"),
        fullDate: d,
      }))
  }

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
  }

  const handleTimeSelect = (fullDate: Date) => {
    setSelectedDate(fullDate)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedDate || !application?.id) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time for your vehicle collection",
        variant: "destructive",
      })
      return
    }

    const selectedSession = sessionDates.find(
      (session) => session.date.getTime() === selectedDate.getTime(),
    )

    if (!selectedSession) {
      toast({
        title: "Invalid Selection",
        description: "The selected time slot is no longer available",
        variant: "destructive",
      })
      return
    }

    // If onComplete is provided, we're in segment mode - skip navigation
    const skipNavigation = !!onComplete

    try {
      await handleVehicleCollectionSubmit(
        selectedDate,
        selectedSession.sessionId,
        selectedSession.depot,
        skipNavigation,
      )

      // Call onComplete callback after successful submission (segment mode)
      if (onComplete) {
        onComplete()
      }
    } catch (error) {
      // Error handling is done in the submission handler
      console.error("Vehicle collection submission failed:", error)
    }
  }

  const isTimeSelected =
    selectedDate &&
    availableTimesForDate(selectedDate).some((t) => t.fullDate.getTime() === selectedDate.getTime())

  return (
    <div className="mx-auto max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Schedule Your Vehicle Collection</CardTitle>
          <CardDescription>
            Select a date and time to collect your delivery vehicle from our operations center
          </CardDescription>
        </CardHeader>
        <CardContent>
          {datesError && (
            <div className="mb-4 rounded-md bg-red-50 p-4 text-red-700">
              Error loading available dates. Please try again later.
            </div>
          )}

          {isLoadingDates && (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-honeycomb"></div>
              <p className="mt-2 text-gray-500">Loading available dates...</p>
            </div>
          )}
          {!isLoadingDates && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <VehicleCollectionCalendar
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    isDateAvailable={isDateAvailable}
                  />

                  <VehicleCollectionTimeSelector
                    selectedDate={selectedDate}
                    availableTimesForDate={availableTimesForDate}
                    onTimeSelect={handleTimeSelect}
                  />
                </div>

                <VehicleCollectionConfirmationSummary
                  selectedDate={selectedDate}
                  isTimeSelected={isTimeSelected}
                  depot={
                    sessionDates.find(
                      (session) =>
                        selectedDate && session.date.getTime() === selectedDate.getTime(),
                    )?.depot
                  }
                />
              </div>

              <VehicleCollectionImportantNotes />

              <Button
                type="submit"
                className="w-full bg-honeycomb text-white hover:bg-amber-500"
                disabled={!isTimeSelected || isCreatingBooking}
              >
                {isCreatingBooking ? (
                  <>
                    <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                    Booking...
                  </>
                ) : (
                  "Confirm Vehicle Collection"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default VehicleCollection
